import { useEffect, useRef, useState } from "react";
import { apiClient } from "../lib/api";

const HEARTBEAT_MS = 30000;
const CAMERA_SAMPLE_MS = 15000;
const IDLE_THRESHOLD_MS = 15000;

/**
 * useFloorActivityMonitor — shift activity monitoring for the Floor.
 *
 * Tracks ACTIVITY LEVEL only (keypress/click/mouse-move counts, idle time,
 * tab focus) — never keystroke content. If the VA grants camera access, a
 * "presence likely" boolean is computed from luminance + frame-to-frame
 * variance entirely in this browser tab; the video frame itself is never
 * sent to or stored on the server. Camera denial doesn't block anything
 * else — the rest of monitoring keeps running.
 *
 * Requires prior consent (`MonitoringConsentGate`) — session/start 403s
 * without it, in which case this just no-ops.
 */
export function useFloorActivityMonitor(enabled) {
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let heartbeatTimer, idleTimer, cameraTimer;

    const counters = { keypresses: 0, clicks: 0, mouse_moves: 0 };
    const idle = { lastActivity: Date.now(), idleAccum: 0, activeAccum: 0 };
    let presenceLikely = null;
    let cameraOn = false;
    let tabFocused = document.visibilityState === "visible";
    let stream = null;

    const bump = () => { idle.lastActivity = Date.now(); };
    const onKeydown = () => { counters.keypresses += 1; bump(); };
    const onClick = () => { counters.clicks += 1; bump(); };
    const onMouseMove = () => { counters.mouse_moves += 1; bump(); };
    const onVisibility = () => { tabFocused = document.visibilityState === "visible"; };

    window.addEventListener("keydown", onKeydown);
    window.addEventListener("click", onClick);
    window.addEventListener("mousemove", onMouseMove);
    document.addEventListener("visibilitychange", onVisibility);

    idleTimer = setInterval(() => {
      const idleMs = Date.now() - idle.lastActivity;
      if (idleMs > IDLE_THRESHOLD_MS) idle.idleAccum += 1;
      else idle.activeAccum += 1;
    }, 1000);

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        cameraOn = true;
        const video = document.createElement("video");
        video.muted = true;
        video.srcObject = stream;
        await video.play().catch(() => {});
        const canvas = document.createElement("canvas");
        canvas.width = 64; canvas.height = 48;
        const ctx = canvas.getContext("2d");
        let prevFrame = null;
        cameraTimer = setInterval(() => {
          try {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const frame = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
            let sum = 0;
            for (let i = 0; i < frame.length; i += 4) sum += frame[i] + frame[i + 1] + frame[i + 2];
            const avgLum = sum / (frame.length / 4) / 3;
            prevFrame = frame; // reserved for future motion-based refinement
            presenceLikely = avgLum > 15; // lit, non-blank feed — never stored
          } catch { /* frame read failed — leave last known value */ }
        }, CAMERA_SAMPLE_MS);
      } catch {
        cameraOn = false; // denied/unavailable — rest of monitoring still runs
      }
    })();

    let sid = null;
    (async () => {
      try {
        const { data } = await apiClient.post("/floor/monitoring/session/start");
        sid = data.session_id;
        sessionIdRef.current = sid;
        if (alive) setSessionId(sid);
      } catch { /* no consent yet, or endpoint unavailable — fail silent */ }
    })();

    heartbeatTimer = setInterval(() => {
      if (!sid) return;
      const payload = {
        keypresses: counters.keypresses,
        clicks: counters.clicks,
        mouse_moves: counters.mouse_moves,
        idle_seconds: idle.idleAccum,
        active_seconds: idle.activeAccum,
        presence_likely: cameraOn ? presenceLikely : null,
        camera_on: cameraOn,
        tab_focused: tabFocused,
      };
      counters.keypresses = 0; counters.clicks = 0; counters.mouse_moves = 0;
      idle.idleAccum = 0; idle.activeAccum = 0;
      apiClient.post(`/floor/monitoring/session/${sid}/heartbeat`, payload).catch(() => {});
    }, HEARTBEAT_MS);

    const endSession = () => {
      const id = sessionIdRef.current;
      if (!id) return;
      sessionIdRef.current = null;
      const token = localStorage.getItem("astat_admin_token");
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/floor/monitoring/session/${id}/end`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          keepalive: true,
        }).catch(() => {});
      } catch { /* */ }
    };
    window.addEventListener("beforeunload", endSession);

    return () => {
      alive = false;
      window.removeEventListener("keydown", onKeydown);
      window.removeEventListener("click", onClick);
      window.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", endSession);
      clearInterval(heartbeatTimer);
      clearInterval(idleTimer);
      clearInterval(cameraTimer);
      if (stream) stream.getTracks().forEach((t) => t.stop());
      endSession();
    };
  }, [enabled]);

  return { sessionId };
}
